import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Divider,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDropDown as ArrowDropDownIcon,
  ArrowRight as ArrowRightIcon,
  Train as TrainIcon,
  FlashOn as FlashOnIcon,
} from '@mui/icons-material';

import styles from './UnitDetailsTree.styles';

const UnitDetailsTree = ({ evnData = [], unitName }) => {
  const [openEVNs, setOpenEVNs] = useState({});
  const navigate = useNavigate();

  const toggleEVN = (id) => setOpenEVNs((p) => ({ ...p, [id]: !p[id] }));

  const baseTrail = unitName
    ? [
        { label: 'Units', href: '/home/units' },
        { label: `Unit ${unitName}`, href: `/home/units/${encodeURIComponent(unitName)}` },
      ]
    : undefined;

  const goToEVN = (e, evn) => {
    e.stopPropagation();
    const evnKey = evn.EVNNumber || evn.name || evn.id;
    const to = `/home/evns/${encodeURIComponent(evnKey)}`;
    navigate(to, { state: baseTrail ? { trail: baseTrail } : undefined });
  };

  const onEVNKey = (e, evn) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goToEVN(e, evn);
    }
  };

  const goToMeter = (e, meter, evn) => {
    e.stopPropagation();

    const key = meter.MeterNumber || meter.name || meter.id;
    const unitKey = meter.Train || unitName || null;
    const evnKey = meter.EVN || evn?.EVNNumber || evn?.EVN || evn?.name || evn?.id || null;
    const params = new URLSearchParams();

    if (unitKey) params.set('unit', unitKey);
    if (evnKey) params.set('evn', evnKey);

    const query = params.toString();
    const to = `/home/meters/${encodeURIComponent(key)}${query ? `?${query}` : ''}`;

    navigate(to, { state: baseTrail ? { trail: baseTrail } : undefined });
  };

  const onMeterKey = (e, meter, evn) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goToMeter(e, meter, evn);
    }
  };

  return (
    <Box sx={styles.treeRoot}>
      <Box sx={styles.unitDetailsTreeContainer}>
        {!!evnData.length && (
          <>
            <Divider sx={styles.unitDetailsTreeDivider} textAlign="left">
              EVNs
            </Divider>

            <List disablePadding>
              {evnData.map((evn) => {
                const isOpen = !!openEVNs[evn.id];
                const evnLabel = evn.name;

                return (
                  <React.Fragment key={evn.id}>
                    <ListItem disableGutters sx={styles.unitDetailsListItem} aria-expanded={isOpen}>
                      <IconButton
                        size="small"
                        disableRipple
                        onClick={() => toggleEVN(evn.id)}
                        aria-label={isOpen ? 'Collapse EVN' : 'Expand EVN'}
                        aria-expanded={isOpen}
                        sx={styles.chevronButton}
                      >
                        {isOpen ? (
                          <ArrowDropDownIcon sx={styles.unitDetailsTreeIcon} />
                        ) : (
                          <ArrowRightIcon sx={styles.unitDetailsTreeIcon} />
                        )}
                      </IconButton>

                      <TrainIcon sx={styles.unitDetailsTreeTrainIcon} />

                      <ListItemText
                        primary={
                          <Typography
                            component="span"
                            role="link"
                            tabIndex={0}
                            onClick={(e) => goToEVN(e, evn)}
                            onKeyDown={(e) => onEVNKey(e, evn)}
                            sx={styles.evnTextLink}
                          >
                            {evnLabel}
                          </Typography>
                        }
                      />
                    </ListItem>

                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      {Array.isArray(evn.meters) && evn.meters.length > 0 && (
                        <Divider sx={styles.unitDetailsTreeMeterDivider} textAlign="left">
                          Meters
                        </Divider>
                      )}
                      <List disablePadding>
                        {(evn.meters || []).map((meter) => {
                          const meterText = meter.MeterNumber || meter.name || 'Unnamed Meter';

                          return (
                            <ListItem
                              key={meter.id}
                              disableGutters
                              sx={styles.unitDetailsTreeMeterButton}
                            >
                              <FlashOnIcon sx={styles.unitDetailsTreeMeterIcon} />
                              <ListItemText
                                primary={
                                  <Typography
                                    component="span"
                                    role="link"
                                    tabIndex={0}
                                    onClick={(e) => goToMeter(e, meter, evn)}
                                    onKeyDown={(e) => onMeterKey(e, meter, evn)}
                                    sx={styles.meterTextLink}
                                  >
                                    {meterText}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </List>
          </>
        )}
      </Box>
    </Box>
  );
};

export default UnitDetailsTree;
